import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';

import Popup from "reactjs-popup";

const popupContentStyle = {
  "background": "var(--od-surface, #fff)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "200px",
  "padding": "0px",
  "zIndex": "1999"
}

const canSubmitForm = (copiedFolderedItem) => {
  return (
    copiedFolderedItem
      && copiedFolderedItem.item
      && copiedFolderedItem.item.human_file_name
      && copiedFolderedItem.item.human_file_name.length > 0
  );
}

const formattedChildrenCount = (folder) => {
  var count = folder.foldered_items_count;

  if(count === 1){
    return  "(" + count + " item)"
  } else {
    return  "(" + count + " items)"
  }
}

const folderedItemTitle = (folderedItem) => {
  var assetName = folderedItem.item.human_file_name;

  if(folderedItem.item.is_folder){
    return (assetName + " " + formattedChildrenCount(folderedItem.item));
  } else {
    return assetName;
  }
}

const folderedItemIsDisabled = (parentFolderedItems, selectedFolderedItems) => {
  var pathIds = parentFolderedItems.map((fi) => fi.id);
  var selectedIds = selectedFolderedItems.map((fi) => fi.id);
  const filtered = pathIds.filter(id => selectedIds.includes(id));

  return filtered.length > 0;
}

const folderedItemIsChecked = (foldered, parentFolderedItems, selectedFolderedItems) => {
  var pathIds = [...parentFolderedItems.map((fi) => fi.id), foldered.id];
  var selectedIds = selectedFolderedItems.map((fi) => fi.id);
  const filtered = pathIds.filter(id => selectedIds.includes(id));

  return filtered.length > 0;
}

const FolderedItemRow = ({
  csrfToken,
  team,
  fetchCalendarEventAttachment,
  currentUser,
  foldered,
  depth,
  toggleFolder,
  selectedFolderedItems,
  togglefolderedItemSelected,
  stageFolderedItemsForDestroy,
  parentFolderedItems,
  copiedFolderedItem,
  copiedFolderedItemChanged,
  copiedFolderedItemNameChanged,
  updateFolderedItemName,
  stageFolderedItemsForMove,
  rootFolder,
  fetchPermissionSet,
  folderedItemUpdated,
  depthWidth,
  fetchGuestsAndPromotersForPermissions,
  calendarEventAttachmentToCopyChanged,
  uploadFilesToFolder,
  showConfirmIdColumn,
  onContextMenu,
  onShare,
  onFolderDownload
}) => {
  const canDrag = currentUser.can_manage_calendar_event_attachments && foldered.item.user_can_change;
  const isFolder = foldered.item.is_folder;
  const isEmpty = isFolder && foldered.item.foldered_items_count === 0;

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: 'foldered-' + foldered.id,
    data: { foldered: foldered },
    disabled: !canDrag
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: 'drop-folder-' + foldered.id,
    data: { foldered: foldered },
    disabled: !isFolder || !foldered.item.can_be_move_target
  });

  const setNodeRef = (node) => {
    setDragRef(node);
    if (isFolder) {
      setDropRef(node);
    }
  };

  const depthStyle = depth > 0 ? { '--folder-depth': depth } : {};
  const [desktopDragOver, setDesktopDragOver] = useState(false);
  const dragCounter = React.useRef(0);

  const handleNativeDragEnter = useCallback((e) => {
    if (!isFolder) return;
    if (e.dataTransfer && e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      dragCounter.current++;
      setDesktopDragOver(true);
    }
  }, [isFolder]);

  const handleNativeDragLeave = useCallback(() => {
    if (!isFolder) return;
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDesktopDragOver(false);
    }
  }, [isFolder]);

  const handleNativeDrop = useCallback(() => {
    dragCounter.current = 0;
    setDesktopDragOver(false);
  }, []);

  return (
    <div
      ref={setNodeRef}
      className={
        "row m-0 control-on-hover-row folder-view-row"
        + (depth > 0 ? " folder-view-row-child" : "")
        + ((isOver && isFolder) || desktopDragOver ? " drag-over" : "")
        + (isDragging ? " dragging" : "")
      }
      data-depth={depth}
      data-folder-id={isFolder ? foldered.item.id : undefined}
      style={depthStyle}
      onDragEnter={handleNativeDragEnter}
      onDragLeave={handleNativeDragLeave}
      onDrop={handleNativeDrop}
      onContextMenu={onContextMenu ? (e) => onContextMenu(e, foldered) : undefined}
    >
      <div className="col-12 p-0 d-flex align-items-center small">
        <div className='d-flex align-items-center folder-view-name-cell'
             data-label="Name"
             style={depthWidth(depth) > 0 ? { paddingLeft: depthWidth(depth) + "px" } : undefined}>
          {canDrag ? (
            <div className="table-gutter drag-handle-gutter"
                 {...attributes}
                 {...listeners}>
              <i className="fas fa-grip-vertical text-muted drag-handle-icon"></i>
            </div>
          ) : null}
          <div className="table-gutter checkbox-table-gutter">
            {currentUser.can_manage_calendar_event_attachments && foldered.item.user_can_change ? (
              <div className="custom-control custom-checkbox custom-checkbox-table">
                <input type="checkbox"
                      disabled={
                        folderedItemIsDisabled(parentFolderedItems, selectedFolderedItems)
                      }
                      checked={
                        folderedItemIsChecked(foldered, parentFolderedItems, selectedFolderedItems)
                      }
                      onChange={
                        (e) => {
                          togglefolderedItemSelected(foldered);
                        }
                      }
                      id={"calendar-event-attachment-" + foldered.id}
                      className="custom-control-input" />
                <label htmlFor={"calendar-event-attachment-" + foldered.id}
                      className="custom-control-label">
                  &nbsp;
                </label>
              </div>
            ) : null}
          </div>
          {foldered.item.is_folder && !isEmpty ? (
            <div className="table-gutter folder-toggle-table-gutter">
              <a href="#"
                onClick={
                  (e) => {
                    e.preventDefault();
                    toggleFolder(foldered.item);
                  }
                }
                className="text-dark">
                {foldered.item.extended ? (
                  <i className="far fa-chevron-down"></i>
                ) : (
                  <i className="far fa-chevron-right"></i>
                )}
              </a>
            </div>
          ) : null}
          {(foldered.item.is_folder || foldered.item.foldable_type === "CalendarEvent") ? (
            <div className="folder-item-icon-wrap">
              {foldered.item.foldable_type === "CalendarEvent" ? (
                <div className="square d-flex align-content-center justify-content-center">
                  <p className="align-self-center mb-0">
                    <i className="far fa-calendar-star folder-item-icon--calendar"></i>
                  </p>
                </div>
              ) : (
                <div className="square d-flex align-content-center justify-content-center">
                  <p className="align-self-center mb-0">
                    <i className={"fas " + foldered.item.fontawesome_icon_class + " folder-item-icon"}></i>
                  </p>
                </div>
              )}
            </div>
          ) : null}
          {copiedFolderedItem.id === foldered.id ? (
            <form onSubmit={
              (e) => {
                e.preventDefault();

                if(!canSubmitForm(copiedFolderedItem)){
                  return false;
                }

                updateFolderedItemName(csrfToken, team, copiedFolderedItem);
              }
            }>
              <div className="input-group">
                <input type="text"
                      autoFocus={true}
                      className="form-control form-control-sm small rename-input"
                      value={copiedFolderedItem.item.human_file_name || ""}
                      onChange={
                        (e) => {
                          copiedFolderedItemNameChanged(e.target.value);
                        }
                      }
                      placeholder="Enter a name" />
                <div className="input-group-append">
                  <button className="btn btn-light btn-sm small text-primary rename-save-btn"
                          disabled={!canSubmitForm(copiedFolderedItem)}
                          type="submit">
                    <strong>Save</strong>
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="text-overflow-ellipsis"
                title={folderedItemTitle(foldered)}>
              <a className={"text-dark" + (isEmpty ? " text-muted" : "")}
                onClick={
                  (e) => {
                    e.preventDefault();
                    if (isEmpty) return;

                    if(foldered.item.is_folder){
                      toggleFolder(foldered.item);
                    } else {
                      fetchCalendarEventAttachment(team, foldered.item.id);
                    }
                  }
                }
                href="#"
                style={isEmpty ? { cursor: 'default' } : undefined}>
                <strong>
                  {foldered.item.human_file_name}
                </strong>
              </a>
              {foldered.item.is_folder ? (
                <span className="folder-count ml-2">
                  {formattedChildrenCount(foldered.item)}
                </span>
              ) : null}
              {foldered.permission_set_id ? (
                <i className="ml-2 far fa-lock small text-danger"></i>
              ) : null}
            </div>
          )}
        </div>
        {showConfirmIdColumn ? (
          <React.Fragment>
            <div
              className="folder-view-event-date-cell"
              data-label="Event Date">
              {foldered.calendar_event_date}
            </div>
            <div
              className="folder-view-event-cell"
              data-label="Event ID">
              {foldered.calendar_event_id}
            </div>
          </React.Fragment>
        ) : null}
        <div
          className="folder-view-date-cell"
          data-label="Date Modified">
          {foldered.item.human_updated_at}
        </div>
        <div
          className="folder-view-size-cell"
          data-label="Size">
          <div>{foldered.item.human_file_size}</div>
          {foldered.item.is_image && foldered.item.human_dimensions ? (
            <div className="folder-view-dimensions-value text-muted">
              {foldered.item.human_dimensions}
            </div>
          ) : null}
        </div>
        <div
          className="folder-view-type-cell"
          data-label="Type">
          {foldered.item.human_extension}
        </div>
        <div
          className='text-right folder-view-actions-cell'
          data-label="">
          {currentUser.can_manage_calendar_event_attachments && foldered.item.user_can_change ? (
            <Popup arrow={false}
                  offsetY={5}
                  position="bottom right"
                  contentStyle={popupContentStyle}
                  onOpen={
                    (e) => {
                      e.preventDefault();
                    }
                  }
                  trigger={open => (
                    <a className="btn btn-external btn-sm control-on-hover-control small ellipsis-btn"
                        onClick={
                          (e) => {
                            e.preventDefault();
                          }
                        }
                        href="#">
                      <i className="far fa-ellipsis-v"></i>
                    </a>
                  )} >
              {close => (
                <div className='row text-left'>
                  <div className="col-12">
                    <ul className="list-group offer-inline-menu popup-inline-menu">
                      {!foldered.item.is_folder ? (
                        <React.Fragment>
                          <li className="list-group-item border-0 py-2 pl-3">
                            <a href={"/teams/" + team.id + "/calendar_event_attachments/" + foldered.item.id + "/download"}
                              className="text-dark">
                              <span className="d-inline-block text-center mr-2 popup-menu-icon">
                                <i className="far fa-arrow-to-bottom fa-fw"></i>
                              </span>
                              Download
                            </a>
                          </li>
                          <li className="list-group-item border-0 py-2 pl-3">
                            <a href="#"
                              onClick={
                                (e) => {
                                  e.preventDefault();
                                  close();
                                  calendarEventAttachmentToCopyChanged(foldered.item);
                                }
                              }
                              className="text-dark">
                              <span className="d-inline-block text-center mr-2 popup-menu-icon">
                                <i className="far fa-copy fa-fw"></i>
                              </span>
                              Copy
                            </a>
                          </li>
                        </React.Fragment>
                      ) : null}
                      {foldered.item.is_folder && foldered.item.can_download ? (
                        <li className="list-group-item border-0 py-2 pl-3">
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              close();
                              onFolderDownload(foldered.item);
                            }}
                            className="text-dark"
                          >
                            <span className="d-inline-block text-center mr-2 popup-menu-icon">
                              <i className="far fa-arrow-to-bottom fa-fw"></i>
                            </span>
                            Download Folder
                          </a>
                        </li>
                      ) : null}
                      <li className="list-group-item border-0 py-2 pl-3">
                        <a href="#"
                          onClick={
                            (e) => {
                              e.preventDefault();
                              close();
                              stageFolderedItemsForMove([foldered]);
                            }
                          }
                          className="text-dark">
                          <span className="d-inline-block text-center mr-2 popup-menu-icon">
                            <i className="fas fa-arrows-alt fa-fw"></i>
                          </span>
                          Move
                        </a>
                      </li>
                      <li className="list-group-item border-0 py-2 pl-3">
                        <a href="#"
                          onClick={
                            (e) => {
                              e.preventDefault();
                              close();
                              copiedFolderedItemChanged(foldered);
                            }
                          }
                          className="text-dark">
                          <span className="d-inline-block text-primary text-center mr-2 popup-menu-icon">
                            <i className="fas fa-pencil fa-fw"></i>
                          </span>
                          Rename
                        </a>
                      </li>
                      {currentUser.can_manage_permission_sets ? (
                        <li className="list-group-item border-0 py-2 pl-3">
                          <a href="#"
                            onClick={
                              (e) => {
                                e.preventDefault();
                                close();

                                var permissionSetToFetch;
                                if(foldered.permission_set_id){
                                  permissionSetToFetch = {
                                    id: foldered.permission_set_id
                                  };
                                } else {
                                  permissionSetToFetch = {
                                    element_id: foldered.id,
                                    element_type: "FolderedItem"
                                  };
                                }

                                permissionSetToFetch = Object.assign({}, permissionSetToFetch, {
                                  _element_type_human_name: "Asset"
                                });

                                if(foldered.calendar_event_id){
                                  fetchGuestsAndPromotersForPermissions(team, foldered.calendar_event_id);
                                }

                                fetchPermissionSet(team, permissionSetToFetch, (permissionSet) => {
                                  var updated = Object.assign({}, foldered, {permission_set_id: permissionSet.id});
                                  folderedItemUpdated(updated);
                                });
                              }
                            }
                            className="text-dark">
                            <span className="d-inline-block text-muted text-center mr-2 popup-menu-icon">
                              <i className="far fa-user-lock fa-fw"></i>
                            </span>
                            Set Permissions
                          </a>
                        </li>
                      ) : null}
                      {onShare ? (
                        <li className="list-group-item border-0 py-2 pl-3">
                          <a href="#"
                            onClick={
                              (e) => {
                                e.preventDefault();
                                close();
                                onShare(foldered);
                              }
                            }
                            className="text-dark">
                            <span className="d-inline-block text-center mr-2 popup-menu-icon">
                              <i className="far fa-link fa-fw"></i>
                            </span>
                            Share
                          </a>
                        </li>
                      ) : null}
                      <li className="list-group-item delete-offer-li border-0 py-2 pl-3">
                        <a href="#"
                          onClick={
                            (e) => {
                              e.preventDefault();
                              close();
                              stageFolderedItemsForDestroy([foldered]);
                            }
                          }
                          className="text-dark">
                          <span className="d-inline-block text-danger text-center mr-2 popup-menu-icon">
                            <i className="fas fa-trash fa-fw"></i>
                          </span>
                          Delete
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </Popup>
          ) : (
            <div>&nbsp;</div>
          )}
        </div>
      </div>
    </div>
  );
};

FolderedItemRow.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  fetchCalendarEventAttachment: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  foldered: PropTypes.object.isRequired,
  depth: PropTypes.number.isRequired,
  toggleFolder: PropTypes.func.isRequired,
  selectedFolderedItems: PropTypes.array,
  togglefolderedItemSelected: PropTypes.func.isRequired,
  stageFolderedItemsForDestroy: PropTypes.func.isRequired,
  parentFolderedItems: PropTypes.array,
  copiedFolderedItem: PropTypes.object,
  copiedFolderedItemChanged: PropTypes.func.isRequired,
  copiedFolderedItemNameChanged: PropTypes.func.isRequired,
  updateFolderedItemName: PropTypes.func.isRequired,
  stageFolderedItemsForMove: PropTypes.func.isRequired,
  rootFolder: PropTypes.object.isRequired,
  fetchPermissionSet: PropTypes.func.isRequired,
  folderedItemUpdated: PropTypes.func.isRequired,
  depthWidth: PropTypes.func.isRequired,
  fetchGuestsAndPromotersForPermissions: PropTypes.func.isRequired,
  calendarEventAttachmentToCopyChanged: PropTypes.func.isRequired,
  uploadFilesToFolder: PropTypes.func.isRequired,
  showConfirmIdColumn: PropTypes.bool,
  onContextMenu: PropTypes.func,
  onShare: PropTypes.func,
  onFolderDownload: PropTypes.func
};

export default FolderedItemRow;
